import { OpenImgContextProvider } from 'openimg/react';
import React from 'react';
import {
  data,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteLoaderData,
  redirect,
} from 'react-router';
import { HoneypotProvider } from 'remix-utils/honeypot/react';
import { type Route } from './+types/root';
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png';
import faviconAssetUrl from './assets/favicons/favicon.svg';
import { GeneralErrorBoundary } from './components/error-boundary.tsx';
import { GlobalProgress } from './components/progress-bar.tsx';
import {
  AnimatedNotification,
  useAnimatedNotification,
} from './components/animated-notification.tsx';
import { NotifyProvider } from './components/notify.tsx';
import { IdleWarningModal } from './components/idle-warning-modal.tsx';
import { useSimpleIdleTimeout } from './hooks/useSimpleIdleTimeout.ts';

// import { UserDropdown } from './components/user-dropdown.tsx';
// import {
//   ThemeSwitch,
//   useOptionalTheme,
// } from './routes/resources+/theme-switch.tsx';
// import carbonStyleSheetUrl from './styles/carbon.css?url';
import tailwindStyleSheetUrl from './styles/tailwind.css?url';
import { getUserId, logout } from './utils/auth.server.ts';
import { getHints, ClientHintCheck } from './utils/client-hints.tsx';
import { prisma } from './utils/db.server.ts';
import { getEnv } from './utils/env.server.ts';
import { pipeHeaders } from './utils/headers.server.ts';
import { honeypot } from './utils/honeypot.server.ts';
import { combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx';
import { useNonce } from './utils/nonce-provider.ts';
import {
  userHasPermission,
  getUserFriendRole,
  userHasRole,
} from './utils/permissions.server.ts';
import { getSystemName, isSystemOnline } from './utils/system-name.server.ts';
import { type Theme, getTheme } from './utils/theme.server.ts';
import { makeTimings, time } from './utils/timing.server.ts';
import { getToast } from './utils/toast.server.ts';
import { TailwindIndicator } from '#app/components/tw-indicator.tsx';

export const links: Route.LinksFunction = () => {
  return [
    // Preload critical fonts to avoid slow loading warnings
    {
      rel: 'preload',
      href: '/fonts/IBM-Plex-Sans/fonts/split/woff2/IBMPlexSans-Regular-Latin1.woff2',
      as: 'font',
      type: 'font/woff2',
      crossOrigin: 'anonymous',
    } as const,
    {
      rel: 'preload',
      href: '/fonts/IBM-Plex-Sans/fonts/split/woff2/IBMPlexSans-Regular-Latin2.woff2',
      as: 'font',
      type: 'font/woff2',
      crossOrigin: 'anonymous',
    } as const,
    {
      rel: 'preload',
      href: '/fonts/IBM-Plex-Sans/fonts/split/woff2/IBMPlexSans-Regular-Latin3.woff2',
      as: 'font',
      type: 'font/woff2',
      crossOrigin: 'anonymous',
    } as const,
    {
      rel: 'preload',
      href: '/fonts/IBM-Plex-Sans/fonts/split/woff2/IBMPlexSans-Regular-Pi.woff2',
      as: 'font',
      type: 'font/woff2',
      crossOrigin: 'anonymous',
    } as const,
    {
      rel: 'preload',
      href: '/fonts/IBM-Plex-Sans/fonts/split/woff2/IBMPlexSans-Regular-Cyrillic.woff2',
      as: 'font',
      type: 'font/woff2',
      crossOrigin: 'anonymous',
    } as const,
    {
      rel: 'preload',
      href: '/fonts/IBM-Plex-Sans/fonts/split/woff2/IBMPlexSans-SemiBold-Latin1.woff2',
      as: 'font',
      type: 'font/woff2',
      crossOrigin: 'anonymous',
    } as const,
    {
      rel: 'preload',
      href: '/fonts/IBM-Plex-Sans/fonts/split/woff2/IBMPlexSans-Light-Latin1.woff2',
      as: 'font',
      type: 'font/woff2',
      crossOrigin: 'anonymous',
    } as const,
    {
      rel: 'preload',
      href: '/fonts/IBM-Plex-Mono/fonts/split/woff2/IBMPlexMono-Regular-Latin1.woff2',
      as: 'font',
      type: 'font/woff2',
      crossOrigin: 'anonymous',
    } as const,
    // Additional SemiBold variants that Carbon might load
    {
      rel: 'preload',
      href: '/fonts/IBM-Plex-Sans/fonts/split/woff2/IBMPlexSans-SemiBold-Latin2.woff2',
      as: 'font',
      type: 'font/woff2',
      crossOrigin: 'anonymous',
    } as const,
    {
      rel: 'preload',
      href: '/fonts/IBM-Plex-Sans/fonts/split/woff2/IBMPlexSans-SemiBold-Latin3.woff2',
      as: 'font',
      type: 'font/woff2',
      crossOrigin: 'anonymous',
    } as const,
    {
      rel: 'preload',
      href: '/fonts/IBM-Plex-Sans/fonts/split/woff2/IBMPlexSans-SemiBold-Pi.woff2',
      as: 'font',
      type: 'font/woff2',
      crossOrigin: 'anonymous',
    } as const,
    {
      rel: 'preload',
      href: '/fonts/IBM-Plex-Sans/fonts/split/woff2/IBMPlexSans-SemiBold-Cyrillic.woff2',
      as: 'font',
      type: 'font/woff2',
      crossOrigin: 'anonymous',
    } as const,
    {
      rel: 'preload',
      href: '/img/cb-logo-black.png',
      as: 'image',
      type: 'image/png',
    } as const,
    {
      rel: 'icon',
      href: '/favicon.ico',
      sizes: '48x48',
    },
    { rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
    { rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
    {
      rel: 'manifest',
      href: '/site.webmanifest',
      crossOrigin: 'use-credentials',
    } as const, // necessary to make typescript happy
    // { rel: 'stylesheet', href: carbonStyleSheetUrl },
    { rel: 'stylesheet', href: tailwindStyleSheetUrl },
  ].filter(Boolean);
};

export const meta: Route.MetaFunction = ({ data }: { data: any }) => {
  const systemName = data?.systemName || 'CircuitBread Insights';
  return [
    { title: data ? systemName : `Error | ${systemName}` },
    { name: 'description', content: `Powered by SwellFox, LLC` },
  ];
};

export async function loader({ request }: Route.LoaderArgs) {
  const timings = makeTimings('root loader');
  const userId = await time(() => getUserId(request), {
    timings,
    type: 'getUserId',
    desc: 'getUserId in root',
  });

  const user = userId
    ? await time(
        () =>
          prisma.user.findUnique({
            select: {
              id: true,
              name: true,
              username: true,
              image: { select: { objectKey: true } },
              roles: {
                select: {
                  name: true,
                  permissions: {
                    select: { entity: true, action: true, access: true },
                  },
                },
              },
            },
            where: { id: userId },
          }),
        { timings, type: 'find user', desc: 'find user in root' },
      )
    : null;
  if (userId && !user) {
    console.info('something weird happened');
    // something weird happened... The user is authenticated but we can't find
    // them in the database. Maybe they were deleted? Let's log them out.
    await logout({ request, redirectTo: '/' });
  }
  const { toast, headers: toastHeaders } = await getToast(request);
  const honeyProps = await honeypot.getInputProps();
  const systemName = await getSystemName();

  // Check if system is online and handle maintenance mode
  const systemOnline = await isSystemOnline();
  const isAdmin = userId ? await userHasRole(userId, 'admin') : false;

  // Define routes that should be accessible during maintenance mode
  const maintenanceAllowedRoutes = [
    '/under-construction',
    '/login',
    '/logout',
    '/auth',
    '/nexus',
    '/verify',
    '/onboarding',
    '/forgot-password',
    '/reset-password',
    '/resources',
    '/healthcheck',
  ];

  const url = new URL(request.url);
  const isAllowedRoute = maintenanceAllowedRoutes.some((route) =>
    url.pathname.startsWith(route),
  );

  // If system is offline and user is not admin, redirect to maintenance page
  // Allow access to authentication routes so admins can log in
  if (!systemOnline && !isAdmin && !isAllowedRoute) {
    throw redirect('/under-construction');
  }

  // Fetch friends for navigation (only if user is authenticated)
  const allFriends = user
    ? await time(
        () =>
          prisma.friend.findMany({
            select: {
              id: true,
              name: true,
              slug: true,
              isFeatured: true,
              isActive: true,
              platformEntries: {
                where: {
                  platformType: {
                    in: ['article', 'youtube'],
                  },
                },
                select: { id: true, platformType: true },
              },
            },
            orderBy: { sortOrder: 'asc' },
          }),
        { timings, type: 'find friends', desc: 'find friends for navigation' },
      )
    : [];

  // Filter friends based on permissions
  const friends = user
    ? await time(
        async () => {
          const filteredFriends = [];
          for (const friend of allFriends) {
            if (friend.slug) {
              const hasPermission = await userHasPermission(
                user.id,
                // @ts-expect-error
                `${friend.slug}:read:any`,
              );

              if (hasPermission) {
                filteredFriends.push(friend);
              }
            }
          }
          return filteredFriends;
        },
        {
          timings,
          type: 'filter friends by permission',
          desc: 'filter friends based on user permissions',
        },
      )
    : [];

  const canViewFriends = user
    ? await time(() => userHasPermission(user.id, 'friends:read:any'), {
        timings,
        type: 'check friends permission',
        desc: 'check if user can view friends',
      })
    : false;

  const canViewPlatforms = user
    ? await time(() => userHasPermission(user.id, 'platforms:read:any'), {
        timings,
        type: 'check platforms permission',
        desc: 'check if user can view platforms',
      })
    : false;

  // Check if user has permission to view settings
  const canViewSettings = user
    ? await time(() => userHasPermission(user.id, 'settings:read:any'), {
        timings,
        type: 'check settings permission',
        desc: 'check if user can view settings',
      })
    : false;

  // Check if user has permission to view users
  const canViewUsers = user
    ? await time(() => userHasPermission(user.id, 'settings-user:read:any'), {
        timings,
        type: 'check users permission',
        desc: 'check if user can view users',
      })
    : false;

  // Check if user has permission to view globals
  const canViewGlobals = user
    ? await time(
        () =>
          userHasPermission(user.id, 'settings-global-badge-reach:read:any'),
        {
          timings,
          type: 'check globals permission',
          desc: 'check if user can view globals',
        },
      )
    : false;

  // Check if user has permission to view settings friends
  const canViewSettingsFriends = user
    ? await time(() => userHasPermission(user.id, 'settings-friend:read:any'), {
        timings,
        type: 'check settings friends permission',
        desc: 'check if user can view settings friends',
      })
    : false;

  // Check if user has permission to view badge reach
  const canViewBadgeReach = user
    ? await time(
        () => userHasPermission(user.id, 'settings-badge-reach:read:any'),
        {
          timings,
          type: 'check badge reach permission',
          desc: 'check if user can view badge reach',
        },
      )
    : false;

  // Check if user has permission to view toolbox
  const canViewToolbox = user
    ? await time(
        () => userHasPermission(user.id, 'settings-toolbox:read:any'),
        {
          timings,
          type: 'check toolbox permission',
          desc: 'check if user can view toolbox',
        },
      )
    : false;

  // Check if user has permission to view importers
  const canViewImporters = user
    ? await time(
        () => userHasPermission(user.id, 'settings-import-access:read:any'),
        {
          timings,
          type: 'check importers permission',
          desc: 'check if user can view importers',
        },
      )
    : false;

  // Check if user has exactly one friend role (for hiding sidebar)
  const userFriendRole = user
    ? await time(() => getUserFriendRole(user.id), {
        timings,
        type: 'check user friend role',
        desc: 'check if user has exactly one friend role',
      })
    : null;

  // Check if user has permission to view dashboard
  const canViewDashboard = user
    ? await time(() => userHasPermission(user.id, 'dashboard:read:any'), {
        timings,
        type: 'check dashboard permission',
        desc: 'check if user can view dashboard',
      })
    : false;

  // Check if user has BOTH admin AND developer roles
  const isAdminAndDeveloper = user
    ? await time(
        async () => {
          const [hasAdminRole, hasDeveloperRole] = await Promise.all([
            userHasRole(user.id, 'admin'),
            userHasRole(user.id, 'developer'),
          ]);
          return hasAdminRole && hasDeveloperRole;
        },
        {
          timings,
          type: 'check admin and developer roles',
          desc: 'check if user has both admin and developer roles',
        },
      )
    : false;

  return data(
    {
      user,
      friends,
      canViewFriends,
      canViewSettings,
      canViewUsers,
      canViewGlobals,
      canViewSettingsFriends,
      canViewBadgeReach,
      canViewToolbox,
      canViewImporters,
      userFriendRole,
      canViewPlatforms,
      canViewDashboard,
      isAdminAndDeveloper,
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
        userPrefs: {
          theme: getTheme(request),
        },
      },
      ENV: getEnv(),
      toast,
      honeyProps,
      systemName,
    },
    {
      headers: combineHeaders(
        { 'Server-Timing': timings.toString() },
        toastHeaders,
      ),
    },
  );
}

export const headers: Route.HeadersFunction = pipeHeaders;

function Document({
  children,
  nonce,
  theme = 'light',
  env = {},
}: {
  children: React.ReactNode;
  nonce: string;
  theme?: Theme;
  env?: Record<string, string | undefined>;
}) {
  const allowIndexing = env.ALLOW_INDEXING !== 'false';
  return (
    <html lang="en" className={`${theme} h-full`} {...({ webcrx: '' } as any)}>
      <head>
        <ClientHintCheck nonce={nonce} />
        <Meta />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        {!allowIndexing && <meta name="robots" content="noindex, nofollow" />}
        <Links />
      </head>
      <body className="bg-background text-foreground">
        {children}
        <script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`,
          }}
          suppressHydrationWarning
        />
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
        <TailwindIndicator />
      </body>
    </html>
  );
}

export function Layout({ children }: { children: React.ReactNode }) {
  // Use useRouteLoaderData as recommended by React Router v7 docs
  // This is safe to use in Layout (unlike useLoaderData)
  const data = useRouteLoaderData<typeof loader>('root');

  // useNonce is now defensive and returns '' if context not ready
  const nonce = useNonce();

  // Force consistent theme to avoid hydration mismatch between browsers
  const theme = data?.requestInfo?.userPrefs?.theme ?? 'light';

  return (
    <Document nonce={nonce} theme={theme} env={data?.ENV}>
      {children}
    </Document>
  );
}

function App() {
  const data = useLoaderData<typeof loader>();
  const {
    notification,
    hideNotification,
    showError,
    showSuccess,
    showWarning,
    showInfo,
  } = useAnimatedNotification();

  // Simple idle timeout - only checks every 5 minutes, no persistent connections
  const idleTimeout = useSimpleIdleTimeout(data.user);

  // Handle toast from loader data
  React.useEffect(() => {
    if (data.toast) {
      if (data.toast.type === 'success') {
        showSuccess(data.toast.title || 'Success', data.toast.description);
      } else if (data.toast.type === 'error') {
        showError(data.toast.title || 'Error', data.toast.description);
      } else {
        showSuccess(data.toast.title || 'Notification', data.toast.description);
      }
    }
  }, [data.toast, showSuccess, showError]);

  const handleLogoutNow = () => {
    // For idle timeout, redirect to login with session expired message and preserve URL
    const currentPath = window.location.pathname + window.location.search;
    const redirectUrl =
      currentPath !== '/'
        ? `/login?redirectTo=${encodeURIComponent(currentPath)}&message=session-expired`
        : '/login?message=session-expired';
    window.location.href = redirectUrl;
  };

  return (
    <OpenImgContextProvider
      optimizerEndpoint="/resources/images"
      getSrc={getImgSrc}
    >
      <NotifyProvider
        showSuccess={showSuccess}
        showError={showError}
        showInfo={showInfo}
        showWarning={showWarning}
      >
        <Outlet />

        {/* Idle timeout warning modal */}
        <IdleWarningModal
          isOpen={idleTimeout.showWarning}
          minutesLeft={idleTimeout.minutesLeft}
          secondsLeft={idleTimeout.secondsLeft}
          onExtendSession={idleTimeout.extendSession}
          onLogoutNow={handleLogoutNow}
        />

        <AnimatedNotification
          show={notification.show}
          kind={notification.kind}
          title={notification.title}
          messages={notification.messages}
          onClose={hideNotification}
          autoHideDelay={notification.config?.autoHideDelay}
          position={notification.config?.position}
          width={notification.config?.width}
          duration={notification.config?.duration}
          offsetFromEdge={notification.config?.offsetFromEdge}
        />
        <GlobalProgress />
      </NotifyProvider>
    </OpenImgContextProvider>
  );
}

function AppWithProviders() {
  const data = useLoaderData<typeof loader>();
  return (
    <HoneypotProvider {...data.honeyProps}>
      <App />
    </HoneypotProvider>
  );
}

export default AppWithProviders;

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary;
